import React from 'react';
import { FileText, Calculator, Percent, ArrowRight, Info, Receipt } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета НДС
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Получаем текстовое представление ставки НДС
  const getTaxRateLabel = (taxRate) => {
    if (taxRate === 'exempt') return 'Без НДС';
    return `${taxRate}%`;
  };

  // Определяем текст результата в зависимости от типа расчета
  const getResultTypeText = () => {
    const isAdding = formData.calculationType === 'add';
    return isAdding ? 'Начисление НДС' : 'Выделение НДС из суммы';
  };

  // Определяем тип расчета (множественные товары или одиночный расчет)
  const isMultipleItems = results.isMultipleItems;

  return (
    <ToolResult title="Результаты расчета НДС">
      {/* Основные метрики для единичного расчета */}
      {!isMultipleItems && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
          {/* Сумма без НДС */}
          <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
            <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
              <FileText className="w-4 h-4"/>
              Сумма без НДС
            </h3>
            <div className="flex items-baseline flex-wrap gap-x-2">
              <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
                {formatCurrency(results.amountWithoutVAT)}
              </span>
            </div>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              {formData.calculationType === 'extract' ? 'Получено из суммы с налогом' : 'Исходная сумма'}
            </p>
          </div>

          {/* НДС и сумма с НДС */}
          <div className="rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
            <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
              <Percent className="w-4 h-4"/>
              {results.isExempt ? 'Без НДС' : 'Сумма НДС'}
            </h3>
            <div className="flex items-baseline flex-wrap gap-x-2">
              <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
                {results.isExempt ? 'Без НДС' : formatCurrency(results.vatAmount)}
              </span>
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                {!results.isExempt && `(${formatNumber((results.vatAmount / results.amountWithoutVAT) * 100, 2)}%)`}
              </span>
            </div>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              {!results.isExempt && (formData.calculationType === 'extract' 
                ? 'НДС выделен из общей суммы' 
                : 'НДС начислен к исходной сумме')}
            </p>
          </div>
        </div>
      )}

      {/* Для множественных товаров показываем общие суммы */}
      {isMultipleItems && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Сумма без НДС */}
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Сумма без НДС</h3>
            <div className="text-xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(results.totalAmountWithoutVAT)}
            </div>
          </div>

          {/* НДС */}
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Сумма НДС</h3>
            <div className="text-xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(results.totalVATAmount)}
            </div>
          </div>

          {/* Сумма с НДС */}
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Сумма с НДС</h3>
            <div className="text-xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(results.totalAmountWithVAT)}
            </div>
          </div>
        </div>
      )}

      {/* Детализированные результаты для одиночного расчета */}
      {!isMultipleItems && (
        <div className="space-y-3 mb-6">
          <ResultItem 
            label="Тип расчета" 
            value={getResultTypeText()} 
            icon={<Calculator className="w-4 h-4"/>}
          />
          
          <ResultItem 
            label="Ставка НДС" 
            value={getTaxRateLabel(results.taxRateOriginal)} 
            color="blue" 
            icon={<Percent className="w-4 h-4"/>}
          />
          
          <ResultItem 
            label="Сумма без НДС" 
            value={formatCurrency(results.amountWithoutVAT)} 
            icon={<Receipt className="w-4 h-4"/>} 
          />
          
          <ResultItem 
            label={results.isExempt ? 'НДС' : 'Сумма НДС'} 
            value={results.isExempt ? 'Без НДС' : formatCurrency(results.vatAmount)} 
            color={results.isExempt ? 'gray' : 'green'}
            icon={<Percent className="w-4 h-4"/>} 
          />
          
          <ResultItem 
            label="Сумма с НДС" 
            value={formatCurrency(results.amountWithVAT)} 
            color="indigo"
            icon={<ArrowRight className="w-4 h-4"/>} 
          />
          
          {formData.includeOtherTaxes && results.otherTaxAmount > 0 && (
            <>
              <ResultItem 
                label={formData.otherTaxName || 'Дополнительный налог'} 
                value={formatCurrency(results.otherTaxAmount)} 
                color="orange" 
              />
              
              <ResultItem 
                label="Итого к оплате" 
                value={formatCurrency(results.totalPayable)} 
                color="purple" 
                bold={true}
              />
            </>
          )}
        </div>
      )}

      {/* Информационная панель о НДС */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mt-2 border border-blue-100 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <Info className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Налог на добавленную стоимость (НДС)</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {formData.calculationType === 'add' 
                ? 'В России НДС начисляется по ставкам 20%, 10% или 0% в зависимости от типа товаров и услуг. НДС уплачивается в бюджет.' 
                : 'Для выделения НДС из суммы используются расчетные ставки 20/120 (16.67%) или 10/110 (9.09%), которые позволяют определить сумму налога в общей стоимости.'}
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;